import type { SalesSubject, SalesVertical, SpendBand, SubjectKind } from '@/client/types/sales';
import { daysAgo, hoursAgo } from './fixtures';

/** Mock ff-prospect index. Source names stay internal — sales UI never shows the portal. */
export type MarketplaceId = 'jiji' | 'meqasa' | 'gpc' | 'lamudi' | 'seventerra';

export interface ActivityPoint {
  at: string;
  count: number;
}

export interface ListingActivity {
  listedSince?: string;
  listingsLast7: number;
  listingsLast30: number;
  avgDaysBetween: number | null;
  history: ActivityPoint[];
}

export interface PromoSpend {
  plan: BoostPlan;
  monthlyGhs: number;
  lifetimeGhs: number;
  boostsLast30: number;
  planFeeGhs: number;
  boostSpendGhs: number;
}

export type BoostPlan = 'none' | 'boosts' | 'featured';

export const BOOST_PLAN_LABEL: Record<BoostPlan, string> = {
  none: 'No paid promo',
  boosts: 'Boosts',
  featured: 'Featured plan',
};

export interface MarketplaceHit {
  id: string;
  marketplace: MarketplaceId;
  collection: string;
  externalId: string;
  displayName: string;
  kind: SubjectKind;
  phone?: string;
  email?: string;
  region: string;
  verticals: SalesVertical[];
  spendBand: SpendBand;
  paid: boolean;
  premium: boolean;
  listingCount: number;
  inventoryValueGhs: number;
  avgPriceGhs: number;
  promoSpend: PromoSpend;
  lastSeen: string;
  categories: string[];
  activity: ListingActivity;
}

function avgPrice(inventory: number, count: number): number {
  if (!count) return 0;
  return Math.round(inventory / count);
}

export function inferActivity(listingCount: number, listedSince?: string): ListingActivity {
  const startYear = listedSince && Number.isFinite(Number(listedSince)) ? Number(listedSince) : 2025;
  const endYear = 2026;
  const endMonth = 8;
  const totalMonths = Math.max(1, (endYear - startYear) * 12 + endMonth + 1);
  const listingsLast30 =
    listingCount <= 0 ? 0 : Math.max(1, Math.round(listingCount * (listingCount > 40 ? 0.22 : 0.32)));
  const listingsLast7 =
    listingCount <= 0 ? 0 : Math.min(listingsLast30, Math.max(listingCount > 20 ? 2 : 1, Math.round(listingsLast30 * 0.3)));
  const avgDaysBetween = listingsLast30 > 0 ? Math.max(1, Math.round(30 / listingsLast30)) : null;
  const history: ActivityPoint[] = [];
  const phase = ((listingCount % 7) / 7) * Math.PI * 2;
  for (let i = 0; i < totalMonths; i++) {
    const y = startYear + Math.floor(i / 12);
    const m = i % 12;
    if (y === endYear && m > endMonth) break;
    const t = totalMonths <= 1 ? 1 : i / (totalMonths - 1);
    const growth = listingCount <= 0 ? 0 : 0.18 + 0.82 * (1 - Math.exp(-2.6 * t));
    const seasonal = 1 + 0.2 * Math.sin((m / 12) * Math.PI * 2 + phase);
    const wobble = 0.8 + ((listingCount * 19 + i * 11) % 21) / 50;
    let count = Math.round(listingsLast30 * growth * seasonal * wobble);
    if (i === totalMonths - 1) count = listingsLast30;
    history.push({ at: `${y}-${String(m + 1).padStart(2, '0')}`, count: Math.max(0, count) });
  }
  return { listedSince, listingsLast7, listingsLast30, avgDaysBetween, history };
}

export function cadenceLabel(activity: ListingActivity): string {
  if (!activity.listingsLast30) return 'Quiet';
  if (activity.avgDaysBetween != null && activity.avgDaysBetween <= 4) return `Every ${activity.avgDaysBetween}d`;
  return `${activity.listingsLast30} in 30d`;
}

export function inferPromoSpend(input: {
  paid: boolean;
  premium: boolean;
  listingsLast30: number;
  inventoryValueGhs: number;
  listedSince?: string;
}): PromoSpend {
  if (!input.paid && !input.premium) {
    return { plan: 'none', monthlyGhs: 0, lifetimeGhs: 0, boostsLast30: 0, planFeeGhs: 0, boostSpendGhs: 0 };
  }
  const inventoryBoost = input.inventoryValueGhs >= 1_000_000 ? 1.55 : input.inventoryValueGhs >= 100_000 ? 1.15 : 1;
  const boostsLast30 = input.premium
    ? Math.max(2, Math.round(input.listingsLast30 * 0.5))
    : Math.max(1, Math.round(input.listingsLast30 * 0.35));
  const boostUnit = input.premium ? 110 : 60;
  const planFeeGhs = input.premium ? Math.round(520 * inventoryBoost) : 0;
  const boostSpendGhs = Math.round(boostsLast30 * boostUnit * inventoryBoost);
  const monthlyGhs = planFeeGhs + boostSpendGhs;
  const year = input.listedSince ? Number(input.listedSince) : NaN;
  const monthsActive = Number.isFinite(year) ? Math.max(4, (2026 - year) * 12 + 8) : 12;
  const ramp = input.premium ? 0.72 : 0.5;
  return {
    plan: input.premium ? 'featured' : 'boosts',
    monthlyGhs,
    lifetimeGhs: Math.round(monthlyGhs * monthsActive * ramp),
    boostsLast30,
    planFeeGhs,
    boostSpendGhs,
  };
}

function hit(
  partial: Omit<MarketplaceHit, 'avgPriceGhs' | 'activity' | 'promoSpend'> & {
    avgPriceGhs?: number;
    promoSpend?: PromoSpend;
    listedSince?: string;
    activity?: ListingActivity;
  },
): MarketplaceHit {
  const { listedSince, activity, promoSpend, ...rest } = partial;
  const resolvedActivity = activity ?? inferActivity(partial.listingCount, listedSince);
  return {
    ...rest,
    avgPriceGhs: partial.avgPriceGhs ?? avgPrice(partial.inventoryValueGhs, partial.listingCount),
    activity: resolvedActivity,
    promoSpend:
      promoSpend ??
      inferPromoSpend({
        paid: partial.paid,
        premium: partial.premium,
        listingsLast30: resolvedActivity.listingsLast30,
        inventoryValueGhs: partial.inventoryValueGhs,
        listedSince,
      }),
  };
}

export const MARKETPLACE_HITS: MarketplaceHit[] = [
  hit({
    id: 'mkt_kwame',
    marketplace: 'meqasa',
    collection: 'meqasa.agent',
    externalId: 'g:4411',
    displayName: 'Kwame Asante',
    kind: 'person',
    phone: '0244111222',
    email: 'kwame.asante@gmail.com',
    region: 'East Legon',
    verticals: ['homes'],
    spendBand: 'high',
    paid: true,
    premium: true,
    listingCount: 14,
    inventoryValueGhs: 2_400_000,
    lastSeen: hoursAgo(18),
    listedSince: '2020',
    categories: ['Houses', 'Apartments'],
  }),
  hit({
    id: 'mkt_yaw_meqasa',
    marketplace: 'meqasa',
    collection: 'meqasa.agent',
    externalId: 'g:9901',
    displayName: 'Yaw Darko Realty',
    kind: 'business',
    phone: '+233277000999',
    email: 'yaw@darkorealty.com',
    region: 'East Legon',
    verticals: ['homes'],
    spendBand: 'high',
    paid: true,
    premium: false,
    listingCount: 22,
    inventoryValueGhs: 5_100_000,
    lastSeen: hoursAgo(6),
    listedSince: '2018',
    categories: ['Houses', 'Land'],
  }),
  hit({
    id: 'mkt_afia',
    marketplace: 'meqasa',
    collection: 'meqasa.agent',
    externalId: 'g:1204',
    displayName: 'Afia Owusu',
    kind: 'person',
    phone: '0246111999',
    email: 'afia.owusu@homes-agent.gh',
    region: 'East Legon',
    verticals: ['homes'],
    spendBand: 'high',
    paid: true,
    premium: true,
    listingCount: 31,
    inventoryValueGhs: 6_400_000,
    lastSeen: hoursAgo(9),
    listedSince: '2019',
    categories: ['Houses', 'Apartments', 'Short stay'],
  }),
  hit({
    id: 'mkt_kejetia',
    marketplace: 'jiji',
    collection: 'jiji.seller',
    externalId: '88012',
    displayName: 'Kejetia Phones Hub',
    kind: 'business',
    phone: '0243888000',
    region: 'Adum',
    verticals: ['market'],
    spendBand: 'mid',
    paid: true,
    premium: false,
    listingCount: 56,
    inventoryValueGhs: 140_000,
    lastSeen: hoursAgo(3),
    listedSince: '2021',
    categories: ['Phones', 'Accessories'],
  }),
  hit({
    id: 'mkt_akua',
    marketplace: 'meqasa',
    collection: 'meqasa.agent',
    externalId: 'g:8801',
    displayName: 'Akua Mensah Realty',
    kind: 'person',
    phone: '0248000777',
    email: 'akua.m@realty.gh',
    region: 'Kumasi',
    verticals: ['homes'],
    spendBand: 'high',
    paid: true,
    premium: false,
    listingCount: 11,
    inventoryValueGhs: 1_800_000,
    lastSeen: hoursAgo(8),
    listedSince: '2022',
    categories: ['Houses'],
  }),
  hit({
    id: 'mkt_circle',
    marketplace: 'jiji',
    collection: 'jiji.seller',
    externalId: '6610',
    displayName: 'Circle Gadgets',
    kind: 'business',
    phone: '0241222333',
    region: 'Accra',
    verticals: ['market'],
    spendBand: 'mid',
    paid: true,
    premium: false,
    listingCount: 32,
    inventoryValueGhs: 96_000,
    lastSeen: hoursAgo(4),
    listedSince: '2020',
    categories: ['Electronics'],
  }),
  hit({
    id: 'mkt_serwaa',
    marketplace: 'jiji',
    collection: 'jiji.seller',
    externalId: '7702',
    displayName: 'Serwaa Motors',
    kind: 'business',
    phone: '0247000555',
    region: 'Kumasi',
    verticals: ['market'],
    spendBand: 'mid',
    paid: true,
    premium: false,
    listingCount: 18,
    inventoryValueGhs: 310_000,
    lastSeen: hoursAgo(10),
    listedSince: '2019',
    categories: ['Cars'],
  }),
  hit({
    id: 'mkt_cantonments',
    marketplace: 'meqasa',
    collection: 'meqasa.agency',
    externalId: 'g:agency-44',
    displayName: 'Cantonments Lets Ltd',
    kind: 'organization',
    phone: '0302445566',
    email: 'hello@cantonmentslets.gh',
    region: 'Accra',
    verticals: ['homes'],
    spendBand: 'high',
    paid: false,
    premium: false,
    listingCount: 0,
    inventoryValueGhs: 0,
    lastSeen: daysAgo(6),
    listedSince: '2023',
    categories: ['Agencies'],
  }),
  hit({
    id: 'mkt_airport_hills',
    marketplace: 'meqasa',
    collection: 'meqasa.agent',
    externalId: 'g:5518',
    displayName: 'Airport Hills Estates',
    kind: 'business',
    phone: '0247001901',
    email: 'sales@airporthills.gh',
    region: 'Accra',
    verticals: ['homes'],
    spendBand: 'high',
    paid: true,
    premium: true,
    listingCount: 48,
    inventoryValueGhs: 12_400_000,
    lastSeen: hoursAgo(5),
    listedSince: '2017',
    categories: ['Houses', 'Developments'],
  }),
  hit({
    id: 'mkt_spintex_auto',
    marketplace: 'jiji',
    collection: 'jiji.seller',
    externalId: '91044',
    displayName: 'Spintex Auto Mall',
    kind: 'business',
    phone: '0247001902',
    region: 'Accra',
    verticals: ['market'],
    spendBand: 'high',
    paid: true,
    premium: true,
    listingCount: 27,
    inventoryValueGhs: 1_960_000,
    lastSeen: hoursAgo(2),
    listedSince: '2018',
    categories: ['Cars', 'SUVs'],
  }),
  hit({
    id: 'mkt_cape_stays',
    marketplace: 'jiji',
    collection: 'jiji.seller',
    externalId: '91045',
    displayName: 'Cape Coast Stays',
    kind: 'business',
    phone: '0247001903',
    email: 'stay@capecoaststays.gh',
    region: 'Cape Coast',
    verticals: ['places'],
    spendBand: 'mid',
    paid: true,
    premium: false,
    listingCount: 9,
    inventoryValueGhs: 0,
    lastSeen: hoursAgo(16),
    listedSince: '2022',
    categories: ['Short stay'],
  }),
  hit({
    id: 'mkt_tamale_agro',
    marketplace: 'jiji',
    collection: 'jiji.seller',
    externalId: '91046',
    displayName: 'Tamale Agro Depot',
    kind: 'business',
    phone: '0247001904',
    region: 'Tamale',
    verticals: ['market'],
    spendBand: 'mid',
    paid: true,
    premium: false,
    listingCount: 41,
    inventoryValueGhs: 88_000,
    lastSeen: hoursAgo(7),
    listedSince: '2021',
    categories: ['Farm inputs'],
  }),
  hit({
    id: 'mkt_achimota_dev',
    marketplace: 'gpc',
    collection: 'gpc.ghana.agent',
    externalId: 'gpc:881',
    displayName: 'Achimota Developers',
    kind: 'organization',
    phone: '0302550101',
    email: 'hello@achimotadev.gh',
    region: 'Accra',
    verticals: ['homes'],
    spendBand: 'high',
    paid: true,
    premium: true,
    listingCount: 19,
    inventoryValueGhs: 7_800_000,
    lastSeen: hoursAgo(20),
    listedSince: '2016',
    categories: ['Developments', 'Land'],
  }),
  hit({
    id: 'mkt_labone_int',
    marketplace: 'lamudi',
    collection: 'lamudi.ghana.agent',
    externalId: 'lamudi:44',
    displayName: 'Labone Interiors Realty',
    kind: 'person',
    phone: '0247001905',
    email: 'nana@laboneinteriors.gh',
    region: 'Osu',
    verticals: ['homes'],
    spendBand: 'high',
    paid: true,
    premium: false,
    listingCount: 16,
    inventoryValueGhs: 3_100_000,
    lastSeen: hoursAgo(28),
    listedSince: '2019',
    categories: ['Apartments'],
  }),
  hit({
    id: 'mkt_kumasi_night',
    marketplace: 'jiji',
    collection: 'jiji.seller',
    externalId: '91047',
    displayName: 'Kumasi Night Market',
    kind: 'business',
    phone: '0247001906',
    region: 'Kumasi',
    verticals: ['market'],
    spendBand: 'mid',
    paid: true,
    premium: false,
    listingCount: 63,
    inventoryValueGhs: 54_000,
    lastSeen: hoursAgo(1),
    listedSince: '2020',
    categories: ['Fashion', 'Wholesale'],
  }),
  hit({
    id: 'mkt_tema_cold',
    marketplace: 'jiji',
    collection: 'jiji.seller',
    externalId: '91048',
    displayName: 'Tema Cold Store',
    kind: 'business',
    phone: '0247001907',
    region: 'Tema',
    verticals: ['market'],
    spendBand: 'mid',
    paid: true,
    premium: false,
    listingCount: 14,
    inventoryValueGhs: 72_000,
    lastSeen: hoursAgo(11),
    listedSince: '2023',
    categories: ['Food'],
  }),
  hit({
    id: 'mkt_dansoman_clinic',
    marketplace: 'seventerra',
    collection: 'seventerra.ghana.agent',
    externalId: 'st:102',
    displayName: 'Dansoman Clinics Group',
    kind: 'organization',
    phone: '0247001908',
    email: 'admin@dansomanclinics.gh',
    region: 'Accra',
    verticals: ['locals'],
    spendBand: 'high',
    paid: true,
    premium: false,
    listingCount: 5,
    inventoryValueGhs: 0,
    lastSeen: daysAgo(2),
    listedSince: '2021',
    categories: ['Health'],
  }),
  hit({
    id: 'mkt_ridge_apts',
    marketplace: 'meqasa',
    collection: 'meqasa.developer',
    externalId: 'g:dev-19',
    displayName: 'Ridge Serviced Apts',
    kind: 'organization',
    phone: '0302660102',
    email: 'stay@ridgeserviced.gh',
    region: 'Accra',
    verticals: ['homes', 'places'],
    spendBand: 'high',
    paid: true,
    premium: true,
    listingCount: 24,
    inventoryValueGhs: 4_600_000,
    lastSeen: hoursAgo(13),
    listedSince: '2018',
    categories: ['Apartments', 'Short stay'],
  }),
];

export function marketplaceHitById(id: string): MarketplaceHit | undefined {
  return MARKETPLACE_HITS.find((h) => h.id === id);
}

export function hitMatchesSubject(hit: MarketplaceHit, subject: Pick<SalesSubject, 'source' | 'matchKeys'>): boolean {
  if (subject.source.collection === hit.collection && subject.source.externalId === hit.externalId) return true;
  const keys = new Set(subject.matchKeys);
  if (hit.marketplace === 'jiji' && keys.has(`jiji:${hit.externalId}`)) return true;
  if ((hit.marketplace === 'meqasa' || hit.collection.startsWith('meqasa')) && keys.has(`meqasa:${hit.externalId}`)) return true;
  return false;
}

export type MarketplaceSort = 'value' | 'count' | 'recent' | 'spend';

export interface MarketplaceQuery {
  q?: string;
  marketplace?: MarketplaceId | 'all';
  vertical?: SalesVertical | 'all';
  region?: string | 'all';
  tier?: 'all' | 'paying' | 'premium' | 'free';
  hasPhone?: boolean;
  notInPool?: boolean;
  recencyDays?: number | 'all';
  listingCategory?: string | 'all';
  sort?: MarketplaceSort;
}

export function marketplaceRegions(): string[] {
  return [...new Set(MARKETPLACE_HITS.map((h) => h.region))].sort();
}

export function marketplaceListingCategories(): string[] {
  return [...new Set(MARKETPLACE_HITS.flatMap((h) => h.categories))].sort();
}
